import {
  type AppType, type ContentRating, type GamblingKind, type IarcAppCategory, IARC_CATEGORIES, IARC_OTHER, GAMBLING_KINDS,
  iarcCategoryFor, IARC_OTHER_APP_QUESTIONS,
} from '@/lib/play-template';
import { Choice, YesNo, QuestionRow, CheckList, Group, SectionTitle, Note } from './controls';

interface Props { value: ContentRating; appType: AppType; onChange: (v: ContentRating) => void }

const NOT_CAPTURED = 'A "yes" here opens follow-up questions the runner has not captured, so it would hand the job to a human.';

export function ContentRatingSection({ value, appType, onChange }: Props) {
  const a = value.answers;
  const setAnswers = (patch: Partial<ContentRating['answers']>) => onChange({ ...value, answers: { ...a, ...patch } });
  const g = a.gambling;
  const other = value.category === 'other';
  const expected = iarcCategoryFor(appType);

  return (
    <div className="space-y-4">
      <SectionTitle sub="The IARC questionnaire under Policy → App content → Content rating. The correspondence email comes from the account, not the template.">
        Content rating
      </SectionTitle>

      <Group title="Category">
        <QuestionRow label="App category">
          <Choice<IarcAppCategory | 'social'>
            value={value.category}
            onChange={(v) => { if (v !== 'social') onChange({ ...value, category: v }); }}
            options={[
              { value: 'game', label: 'Game', disabled: expected !== 'game', title: expected !== 'game' ? 'The app type is App' : undefined },
              { value: 'social', label: 'Social or communication', disabled: true, title: 'Not captured by the runner yet' },
              { value: 'other', label: 'All other app types', disabled: expected !== 'other', title: expected !== 'other' ? 'The app type is Game' : undefined },
            ]}
          />
        </QuestionRow>
        <p className="text-xs text-content-muted">
          Follows the app type on the App content tab.
        </p>
      </Group>

      {other && (
        <Group title="All other app types — answered “no” throughout">
          {IARC_OTHER_APP_QUESTIONS.map((q) => (
            <QuestionRow key={q.question} label={q.question} hint={q.card}>
              <span className="text-sm text-content-muted">No</span>
            </QuestionRow>
          ))}
          <p className="text-xs text-content-muted">
            The runner answers this questionnaire as written here. An app that needs a “yes” anywhere (in-app purchases,
            chat, online content) is rated by hand in the console.
          </p>
        </Group>
      )}

      {!other && (<>
      <Group title="Does the game contain…">
        {IARC_CATEGORIES.map((c) => (
          <QuestionRow key={c.id} label={c.label} note={a[c.id] && NOT_CAPTURED}>
            <YesNo value={a[c.id]} onChange={(v) => setAnswers({ [c.id]: v } as Partial<ContentRating['answers']>)} />
          </QuestionRow>
        ))}

        <QuestionRow label="Gambling" hint="Themes, simulated gambling, lotteries, or real money.">
          <YesNo value={g.present} onChange={(v) => setAnswers({ gambling: { ...g, present: v } })} />
        </QuestionRow>
        {g.present && (
          <div className="pl-4 border-l-2 border-border space-y-4">
            <div>
              <div className="text-sm text-content mb-1.5">What kind of gambling</div>
              <CheckList<GamblingKind>
                value={g.kinds}
                onChange={(v) => setAnswers({ gambling: { ...g, kinds: GAMBLING_KINDS.map((k) => k.id).filter((k) => v.includes(k)) } })}
                options={GAMBLING_KINDS.map((k) => ({
                  value: k.id, label: k.label,
                  hint: k.id === 'real_money' ? 'A policy matter a human has to handle' : undefined,
                }))}
              />
              {g.kinds.includes('real_money') && <Note>Real-money gambling needs licences and a human review; the runner will not submit it.</Note>}
            </div>
            <QuestionRow label="Is gambling the central theme of the game?">
              <YesNo value={g.central} onChange={(v) => setAnswers({ gambling: { ...g, central: v } })} />
            </QuestionRow>
          </div>
        )}
      </Group>

      <Group title="Miscellaneous">
        {IARC_OTHER.map((c) => (
          <QuestionRow key={c.id} label={c.label} note={a.other[c.id] && NOT_CAPTURED}>
            <YesNo value={a.other[c.id]} onChange={(v) => setAnswers({ other: { ...a.other, [c.id]: v } })} />
          </QuestionRow>
        ))}
      </Group>
      </>)}
    </div>
  );
}
